import {observer} from "mobx-react";
import React from "react";
import Currency from "../../../../../components/currency/currency";
import DateTimeV2, {DATETIME_VIEW} from "../../../../../components/datetime-v2/datetime-v2";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import FontBody4 from "../../../../../components/fonts/font-body4/font-body4";
import {FONT_COLOR, FONT_VARIANT} from "../../../../../components/fonts/font-props";
import IconEnvelopOpenDollar from "../../../../../components/icons/icon-envelop-open-dollar/icon-envelop-open-dollar";
import Icon from "../../../../../components/icons/icon/icon";
import GridLine from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import GridLineItem from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line-item/grid-line-item";
import "./rma-payments-exchanges-card.scss";
import {GridColumn} from "../../../../../components/layouts/hooks/grid-style-hook";
import {PAYMENT_STATUS} from "../../rma-types";
import PaymentStore from "../../stores/payment/payment-store";
import PaymentStatusChip from "./payment-status-chip";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { useDateTimeFormattingParams } from "../../../../../hooks/use-datetime-formatting-params";

interface IDraftOrderGridLineProps {
    store: PaymentStore;
}

interface IColumnsCellsWithSettings {
    settings: GridColumn;
    cell: React.ReactNode;
}

const PaymentGridLine: React.FC<IDraftOrderGridLineProps> = observer(({ store }) => {

    let columns: IColumnsCellsWithSettings[] = [];

    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-returns-rma-card';
    const dateTimeParams = useDateTimeFormattingParams();

    columns.push({
        settings: { width: 200, type: 'px' },
        cell: (
            <GridLineItem>
                <div className={'rma-payments-exchanges'}>
                    <Icon Component={IconEnvelopOpenDollar} />
                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('PORTAL_PAYMENT', pageScope)}</FontBody1>
                </div>
            </GridLineItem>
        ),
    });
    
    if (store.amount && store.currency) {
        columns.push({
            settings: { width: 2 },
            cell: (
                <GridLineItem>
                    <div className={'rma-payments-exchanges-content'}>
                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('PAYMENT_COLUMN_TITLE_FOR_AUTHORIZED', pageScope)}</FontBody4>
                        <FontBody4 color={FONT_COLOR.PRIMARY3_50}>
                            <Currency currencyCode={store.currency} amount={store.amount} />
                        </FontBody4>
                    </div>
                </GridLineItem>
            ),
        });
    }

    if (store.authorizedDate) {
        columns.push({
            settings: { width: 2 },
            cell: (
                <GridLineItem>
                    <div className={'rma-payments-exchanges-content'}>
                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('PAYMENT_COLUMN_TITLE_FOR_AUTHORIZATION_DATE', pageScope)}</FontBody4>
                        <FontBody4 color={FONT_COLOR.PRIMARY3_50}>
                            <DateTimeV2 {...dateTimeParams} utcDateString={store.authorizedDate} dateTimeView={DATETIME_VIEW.DATE_ONLY}/>
                        </FontBody4>
                    </div>
                </GridLineItem>
            ),
        });
    }

    if (store.authorizationExpirationDate) {
        const columnWidth = store.cancelDate && store.status === PAYMENT_STATUS.CANCELED ? 2 : 4;
        columns.push({
            settings: { width: columnWidth },
            cell: (
                <GridLineItem>
                    <div className={'rma-payments-exchanges-content'}>
                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('PAYMENT_COLUMN_TITLE_FOR_EXPIRATION_DATE', pageScope)}</FontBody4>
                        <FontBody4 color={FONT_COLOR.PRIMARY3_50}>
                            {<DateTimeV2 {...dateTimeParams} utcDateString={store.authorizationExpirationDate} dateTimeView={DATETIME_VIEW.DATE_ONLY}/>}
                            {store.authorizationExpirationDaysLeft && (
                                store.authorizationExpirationDaysLeft === 1
                                    ? ` (${getTranslation('PAYMENT_VALUE_AUTHORIZATION_EXPIRATION_DAYS_SINGULAR', pageScope, [store.authorizationExpirationDaysLeft.toString()])})`
                                    : ` (${getTranslation('PAYMENT_VALUE_AUTHORIZATION_EXPIRATION_DAYS_PLURAL', pageScope, [store.authorizationExpirationDaysLeft.toString()])})`
                            )}
                        </FontBody4>
                    </div>
                </GridLineItem>
            ),
        });
    }

    if (store.cancelDate && store.status === PAYMENT_STATUS.CANCELED) {
        const columnWidth = store.authorizationExpirationDate ? 2 : 4;
        columns.push({
            settings: { width: columnWidth },
            cell: (
                <GridLineItem>
                    <div className={'rma-payments-exchanges-content'}>
                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('PAYMENT_COLUMN_TITLE_FOR_CANCEL_DATE', pageScope)}</FontBody4>
                        <FontBody4 color={FONT_COLOR.PRIMARY3_50}>
                            {<DateTimeV2 {...dateTimeParams} utcDateString={store.cancelDate} dateTimeView={DATETIME_VIEW.DATE_ONLY}/>}
                        </FontBody4>
                    </div>
                </GridLineItem>
            ),
        });
    }

    if (store.captureDate && store.capturedAmount && store.status === PAYMENT_STATUS.CHARGED) {
        columns.push({
            settings: { width: 2 },
            cell: (
                <GridLineItem>
                    <div className={'rma-payments-exchanges-content'}>
                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('PAYMENT_COLUMN_TITLE_FOR_CAPTURED', pageScope)}</FontBody4>
                        <FontBody4 color={FONT_COLOR.PRIMARY3_50}>
                            <Currency currencyCode={store.currency} amount={store.capturedAmount} />
                        </FontBody4>
                    </div>
                </GridLineItem>
            ),
        });
        columns.push({
            settings: { width: 2 },
            cell: (
                <GridLineItem>
                    <div className={'rma-payments-exchanges-content'}>
                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('PAYMENT_COLUMN_TITLE_FOR_CAPTURE_DATE', pageScope)}</FontBody4>

                        <FontBody4 color={FONT_COLOR.PRIMARY3_50}>
                            {<DateTimeV2 {...dateTimeParams} utcDateString={store.captureDate} dateTimeView={DATETIME_VIEW.DATE_ONLY}/>}
                        </FontBody4>
                    </div>
                </GridLineItem>
            ),
        });
    }
    columns.push({
        settings: { width: 100, type: 'px' },
        cell: (
            <GridLineItem>
                <div className={'rma-payments-exchanges-content'}>
                    <PaymentStatusChip paymentStatus={store.status} />
                </div>
            </GridLineItem>
        ),
    });

    return (
        <GridLine
            key={`rma_page_payment_${store.paymentId}`}
            settings={{
                columns: columns.map((column) => column.settings), 
            }}
            columns={columns.map((column) => column.cell)} />
    )
});

export default PaymentGridLine;